import { memo } from "react";
import styles from "../styles/SchoolFeedWidget.module.scss";
import adminStyles from "../../admin/sidebar/pages/styles/AddNewSourcePage.module.scss";
import FontAwesome from "../../utilities/FontAwesome";
import { IMinimalOrganization } from "../../../utils/organizationSharingUtils";
import { useStores } from "../../../stores/useStores";

export interface SharePathsEditorProps {
  selectedOrganizations: IMinimalOrganization[];
  shareRequestOrganizations: IMinimalOrganization[];
  onOrganizationsChange: (orgs: IMinimalOrganization[]) => void | Promise<void>;
  restrictToCurrentOrgAndBelow?: boolean;
  allowSelectNonEditorOrganizations?: boolean;
  isLoading?: boolean;
}

export const SharePathsEditor = memo(function SharePathsEditor(
  props: SharePathsEditorProps,
) {
  const { modalStore, organizationStore } = useStores();

  const handleRemoveOrganization = (orgId: string) => {
    const allOrgs = [
      ...props.selectedOrganizations,
      ...(props.shareRequestOrganizations || []),
    ];
    const newOrgs = allOrgs.filter((o) => o.id !== orgId);
    props.onOrganizationsChange(newOrgs);
  };

  return (
    <div
      className={adminStyles.shareContainer}
      data-is-loading={props.isLoading || false}
    >
      <div className={adminStyles.shareHeader}>
        <span>Content Targets</span>
        <small> - Select the pages where the content should appear.</small>
      </div>

      <div className={adminStyles.primaryLocation}>
        <span className={adminStyles.primaryLabel}>Primary Location:</span>
        <span className={adminStyles.selectedOrg} data-is-current={true}>
          {props.selectedOrganizations.find((org) => org.is_primary)?.title ||
            organizationStore.currentOrganization.title}
          <span className={adminStyles.currentOrgIndicator}>(current)</span>
        </span>
      </div>

      <div className={adminStyles.sharedLocations}>
        <span className={adminStyles.sharedLabel}>
          Secondary Page
          {props.selectedOrganizations.length -
            1 +
            (props.shareRequestOrganizations?.length || 0) ===
          1
            ? ""
            : "s"}
          :
        </span>
        {props.selectedOrganizations.length +
          (props.shareRequestOrganizations?.length || 0) <=
        1 ? (
          <button
            className={adminStyles.addSecondaryPagesLink}
            onClick={() => {
              const initialOrgs = [
                ...props.selectedOrganizations,
                ...(props.shareRequestOrganizations || []),
              ];

              modalStore.addModal({
                type: "organizationSelection",
                initialSelectedOrganizations: initialOrgs,
                setOrganizations: props.onOrganizationsChange,
                multiSelect: true,
                allowSelectNonEditorOrganizations:
                  props.allowSelectNonEditorOrganizations ?? true,
                restrictToCurrentOrgAndBelow:
                  props.restrictToCurrentOrgAndBelow ?? false,
              });
            }}
          >
            Add Secondary Pages
          </button>
        ) : (
          <>
            <div className={adminStyles.selectedOrgs}>
              {props.selectedOrganizations
                .filter((org) => !org.is_primary)
                .map((org, idx) => (
                  <span
                    key={idx}
                    className={adminStyles.selectedOrg}
                    data-is-request={false}
                  >
                    {org.title}
                    {!org.preventDeselect && (
                      <button
                        onClick={() => handleRemoveOrganization(org.id)}
                        disabled={props.isLoading}
                      >
                        <FontAwesome
                          prefix={"fas"}
                          name={props.isLoading ? "fa-spinner" : "fa-times"}
                          spin={props.isLoading}
                        />
                      </button>
                    )}
                  </span>
                ))}
              {(props.shareRequestOrganizations || []).map((org, idx) => (
                <span
                  key={`pending-${idx}`}
                  className={adminStyles.selectedOrg}
                  data-is-request={true}
                >
                  <span
                    className={styles.tooltip}
                    data-content={"Pending approval by organization admin"}
                  >
                    <FontAwesome name={"fa-solid fa-circle-info"} />
                  </span>{" "}
                  {org.title}
                  <button
                    onClick={() => handleRemoveOrganization(org.id)}
                    disabled={props.isLoading}
                  >
                    <FontAwesome
                      prefix={"fas"}
                      name={props.isLoading ? "fa-spinner" : "fa-times"}
                      spin={props.isLoading}
                    />
                  </button>
                </span>
              ))}
            </div>
            <button
              className={adminStyles.editSecondaryPagesLink}
              onClick={() => {
                const initialOrgs = [
                  ...props.selectedOrganizations,
                  ...(props.shareRequestOrganizations || []),
                ];

                modalStore.addModal({
                  type: "organizationSelection",
                  initialSelectedOrganizations: initialOrgs,
                  setOrganizations: props.onOrganizationsChange,
                  multiSelect: true,
                  allowSelectNonEditorOrganizations:
                    props.allowSelectNonEditorOrganizations ?? true,
                  restrictToCurrentOrgAndBelow:
                    props.restrictToCurrentOrgAndBelow ?? false,
                });
              }}
            >
              Edit Secondary Pages
            </button>
          </>
        )}
      </div>
    </div>
  );
});
